import React from 'react';
import { GREEN, DANGER, WARNING, SUBTLE, HAIRLINE, MUTED, CYAN, MAGENTA, DISCO } from './tokens';
import { useSoft, SOFT } from './SurfaceStyleContext';

/** Named pill treatments from the handoff. Each is { bg, fg, border }. */
export const PILL = {
  black: { bg: '#000', fg: '#fff', border: '#000' },
  outline: { bg: '#fff', fg: '#000', border: '#000' },
  green: { bg: GREEN, fg: '#fff', border: GREEN },
  yellow: { bg: WARNING, fg: '#000', border: WARNING },
  grey: { bg: SUBTLE, fg: '#000', border: SUBTLE },
  hairline: { bg: HAIRLINE, fg: '#000', border: HAIRLINE },
  ghost: { bg: '#fff', fg: MUTED, border: HAIRLINE },
  redOutline: { bg: '#fff', fg: DANGER, border: DANGER },
  red: { bg: DANGER, fg: '#fff', border: DANGER },
  cyan: { bg: CYAN, fg: '#000', border: '#000' },
  magenta: { bg: MAGENTA, fg: '#000', border: '#000' },
  glitchGreen: { bg: DISCO.green, fg: '#000', border: '#000' },
  yellowBordered: { bg: WARNING, fg: '#000', border: '#000' },
  lilac: { bg: DISCO.lilac, fg: '#000', border: '#000' },
  mint: { bg: DISCO.mint, fg: '#000', border: '#000' },
  lime: { bg: DISCO.lime, fg: '#000', border: '#000' },
  blush: { bg: DISCO.blush, fg: '#000', border: '#000' },
  discoYellow: { bg: DISCO.yellow, fg: '#000', border: '#000' },
  subtleBordered: { bg: SUBTLE, fg: '#000', border: '#000' },
};

/**
 * Status pill. `tone` picks a PILL preset; bg/fg/border override it.
 * size: 'md' (10px, 5×8) for header pills · 'sm' (9px, 3×7) for row statuses · 'xs' (9px, 2×6) tags.
 */
/**
 * The soft vocabulary's colours (handoff §6): guaranteed black / white, Party Starter magenta /
 * white, FREE #22C55E / black, CURATED lilac / black, LISTED #E5E7EB / black, NEEDS A HOST
 * #FACC15 / black. Everything else keeps its hard colour and drops the border.
 */
export const SOFT_PILL = {
  black: { bg: '#000', fg: '#fff' },
  outline: { bg: SOFT.chip, fg: '#000' },
  green: { bg: SOFT.green, fg: '#fff' },
  yellow: { bg: SOFT.hostWanted, fg: '#000' },
  grey: { bg: SOFT.listed, fg: '#000' },
  hairline: { bg: SOFT.chip, fg: '#000' },
  ghost: { bg: SOFT.chip, fg: SOFT.muted },
  redOutline: { bg: SOFT.redTint, fg: SOFT.red },
  red: { bg: SOFT.red, fg: '#fff' },
  cyan: { bg: SOFT.cyan, fg: '#000' },
  magenta: { bg: SOFT.magenta, fg: '#fff' },
  glitchGreen: { bg: SOFT.freePill, fg: '#000' },
  yellowBordered: { bg: SOFT.hostWanted, fg: '#000' },
  lilac: { bg: SOFT.lilac, fg: '#000' },
  mint: { bg: DISCO.mint, fg: '#000' },
  lime: { bg: DISCO.lime, fg: '#000' },
  blush: { bg: DISCO.blush, fg: '#000' },
  discoYellow: { bg: DISCO.yellow, fg: '#000' },
  subtleBordered: { bg: SOFT.listed, fg: '#000' },
};

export default function Pill({ tone = 'outline', bg, fg, border, size = 'md', className = '', style = {}, children, ...rest }) {
  const soft = useSoft();
  const t = PILL[tone] || PILL.outline;
  const sizes = {
    md: { fontSize: 10, padding: '5px 8px' },
    sm: { fontSize: 9, padding: '3px 7px' },
    xs: { fontSize: 9, padding: '2px 6px' },
  };
  if (soft) {
    const st = SOFT_PILL[tone] || SOFT_PILL.outline;
    const softSizes = {
      md: { fontSize: 10, padding: '5px 12px', letterSpacing: '0.14em' },
      sm: { fontSize: 9, padding: '4px 10px', letterSpacing: '0.14em' },
      xs: { fontSize: 8, padding: '3px 8px', letterSpacing: '0.14em' },
    };
    return (
      <span
        className={`inline-block flex-shrink-0 rounded-full font-mono font-bold uppercase ${className}`}
        style={{ background: bg || st.bg, color: fg || st.fg, lineHeight: 1.2, ...softSizes[size], ...style }}
        {...rest}
      >
        {children}
      </span>
    );
  }
  return (
    <span
      className={`inline-block flex-shrink-0 border-2 font-mono font-bold uppercase ${className}`}
      style={{ background: bg || t.bg, color: fg || t.fg, borderColor: border || t.border, letterSpacing: '0.1em', lineHeight: 1.2, ...sizes[size], ...style }}
      {...rest}
    >
      {children}
    </span>
  );
}
